/**
 * @module services/supabaseClient
 * @description Supabase client for Preparo proxy mode
 *
 * When proxy mode is enabled, this client handles:
 * - Authentication with Spectral Cloud Portal
 * - Proxying Pandoc API requests through Edge Functions
 *
 * The API configuration is stored server-side and never exposed to the client.
 */

import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js';
import { api } from '@/config';
import { createLogger } from './loggerService';

const log = createLogger('supabase');

// =============================================================================
// CLIENT INITIALIZATION
// =============================================================================

let supabaseClient: SupabaseClient | null = null;

/**
 * Get or create Supabase client
 */
export function getSupabaseClient(): SupabaseClient | null {
  if (!api.proxy.enabled) {
    return null;
  }

  if (!api.proxy.supabaseUrl || !api.proxy.supabaseAnonKey) {
    log.warn('Supabase credentials not configured for proxy mode');
    return null;
  }

  if (!supabaseClient) {
    supabaseClient = createClient(api.proxy.supabaseUrl, api.proxy.supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
    log.info('Supabase client initialized');
  }

  return supabaseClient;
}

/**
 * Handle SSO tokens from URL hash (from Portal redirect)
 * Called on app initialization to check for and process SSO tokens
 */
export async function handleSsoFromUrl(): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;

  // Check for SSO tokens in URL hash
  const hash = window.location.hash.substring(1);
  if (!hash) return false;

  const params = new URLSearchParams(hash);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const type = params.get('type');

  // Only process if it looks like SSO tokens from Portal
  if (type !== 'sso' || !accessToken || !refreshToken) {
    return false;
  }

  log.info('SSO tokens detected in URL, setting session');

  try {
    const { data, error } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      log.error('Failed to set session from SSO tokens', { error: error.message });
      return false;
    }

    // Clear the URL hash after successful session set
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    log.info('SSO session set successfully', { userId: data.user?.id });
    return true;
  } catch (error) {
    log.error('Error handling SSO tokens', { error });
    return false;
  }
}

// =============================================================================
// SESSION MANAGEMENT
// =============================================================================

/**
 * Get current session
 */
export async function getSession(): Promise<Session | null> {
  const client = getSupabaseClient();
  if (!client) return null;

  const { data } = await client.auth.getSession();
  return data.session;
}

/**
 * Get access token for Edge Function calls
 */
export async function getAccessToken(): Promise<string | null> {
  const session = await getSession();
  return session?.access_token || null;
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getSession();
  return !!session;
}

/**
 * Sign out from Supabase
 */
export async function signOut(): Promise<void> {
  const client = getSupabaseClient();
  if (client) {
    await client.auth.signOut();
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  const client = getSupabaseClient();
  if (!client) {
    return { success: false, error: 'Supabase client not available' };
  }

  const { data, error } = await client.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    log.error('Sign in failed', { error: error.message });
    return { success: false, error: error.message };
  }

  log.info('Sign in successful', { userId: data.user?.id });
  return { success: true };
}

// =============================================================================
// PANDOC PROXY
// =============================================================================

interface ProxyRequestOptions {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  /** Expected response type - 'json' (default), 'text', or 'blob' */
  responseType?: 'json' | 'text' | 'blob';
  /** Timeout in milliseconds */
  timeout?: number;
}

interface ProxyResponse<T> {
  data: T;
  headers: {
    contentType?: string;
  };
}

/**
 * Make a proxied request to Pandoc API via Edge Function
 */
export async function proxyPandocRequest<T>(
  options: ProxyRequestOptions
): Promise<ProxyResponse<T>> {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase client not available');
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const { path, method = 'GET', body, params, responseType = 'json', timeout = 60000 } = options;

  // Build the proxy URL
  const proxyUrl = `${api.proxy.supabaseUrl}/functions/v1/${api.proxy.pandocProxyFunction}`;

  // Build query params for GET requests
  let url = proxyUrl;
  if (method === 'GET' && params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url = `${proxyUrl}?${queryString}`;
    }
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    apikey: api.proxy.supabaseAnonKey,
    'Content-Type': 'application/json',
  };

  // For GET requests, pass the Pandoc path via header
  if (method === 'GET') {
    headers['x-pandoc-path'] = path;
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const fetchOptions: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    };

    // For non-GET requests, include path and body in request body
    if (method !== 'GET') {
      fetchOptions.body = JSON.stringify({
        path,
        method,
        body,
        params,
      });
    }

    log.debug('Proxy request', { path, method });

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle specific error codes
      if (errorData.code === 'NO_CONFIG') {
        throw new Error(
          'Pandoc server not configured. Please configure it in Spectral Cloud Portal settings.'
        );
      }

      throw new Error(errorData.error || `Proxy request failed: ${response.status}`);
    }

    let data: T;
    if (responseType === 'blob') {
      data = (await response.blob()) as unknown as T;
    } else if (responseType === 'text') {
      data = (await response.text()) as unknown as T;
    } else {
      const text = await response.text();
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          log.warn('Failed to parse response as JSON', { text: text.substring(0, 100) });
          data = text as unknown as T;
        }
      } else {
        data = null as unknown as T;
      }
    }

    return {
      data,
      headers: {
        contentType: response.headers.get('Content-Type') || undefined,
      },
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Check if Pandoc server is configured in Spectral Cloud
 */
export async function isPandocConfigured(): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;

  const accessToken = await getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(`${api.proxy.supabaseUrl}/functions/v1/api-keys`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: api.proxy.supabaseAnonKey,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return false;

    const { keys } = await response.json();
    // For now, we assume Pandoc is always configured if the user is authenticated
    // In the future, we could check for a specific 'pandoc' key
    return true;
  } catch {
    return false;
  }
}

// =============================================================================
// ZOTERO PROXY
// =============================================================================

interface ZoteroProxyRequestOptions {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  zoteroHeaders?: Record<string, string>;
  signal?: AbortSignal;
}

interface ZoteroProxyResponse<T> {
  data: T;
  headers: {
    totalResults?: number;
    lastModifiedVersion?: number;
    link?: string;
  };
}

/**
 * Make a proxied request to Zotero API via Edge Function
 *
 * Uses the same approach as Refero:
 * - GET requests: path via x-zotero-path header, params in URL query string
 * - Other methods: everything in JSON body
 */
export async function proxyZoteroRequest<T>(
  options: ZoteroProxyRequestOptions
): Promise<ZoteroProxyResponse<T>> {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error('Supabase client not available');
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  const { path, method = 'GET', body, params, zoteroHeaders, signal } = options;

  // Build the proxy URL
  let proxyUrl = `${api.proxy.supabaseUrl}/functions/v1/zotero-proxy`;

  // Build query params for GET requests (add to URL)
  if (method === 'GET' && params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.set(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      proxyUrl = `${proxyUrl}?${queryString}`;
    }
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    apikey: api.proxy.supabaseAnonKey,
    'Content-Type': 'application/json',
  };

  // For GET requests, pass the Zotero path via header
  if (method === 'GET') {
    headers['x-zotero-path'] = path;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    signal,
  };

  // For non-GET requests, include path, headers, and body in request body
  if (method !== 'GET') {
    fetchOptions.body = JSON.stringify({
      path,
      method,
      body,
      params,
      headers: zoteroHeaders, // Edge Function expects 'headers', not 'zoteroHeaders'
    });
  }

  log.debug('Zotero proxy request', { path, method });

  const response = await fetch(proxyUrl, fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle specific error codes
    if (errorData.code === 'NO_API_KEY') {
      throw new Error(
        'Zotero API key not configured. Please configure it in Spectral Cloud Portal settings.'
      );
    }

    throw new Error(errorData.error || `Zotero proxy request failed: ${response.status}`);
  }

  // Handle empty responses (e.g., 204 No Content) gracefully
  const text = await response.text();
  let data: T;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      log.warn('Failed to parse response as JSON', { text: text.substring(0, 100) });
      data = text as unknown as T;
    }
  } else {
    data = null as unknown as T;
  }

  // Extract headers from the HTTP response headers (set by the Edge Function)
  const totalResultsHeader = response.headers.get('Total-Results');
  const lastModifiedVersionHeader = response.headers.get('Last-Modified-Version');
  const linkHeader = response.headers.get('Link');

  return {
    data,
    headers: {
      totalResults: totalResultsHeader ? parseInt(totalResultsHeader, 10) : undefined,
      lastModifiedVersion: lastModifiedVersionHeader ? parseInt(lastModifiedVersionHeader, 10) : undefined,
      link: linkHeader || undefined,
    },
  };
}

/**
 * Check if Zotero API key is configured in Spectral Cloud
 */
export async function isZoteroConfigured(): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;

  const accessToken = await getAccessToken();
  if (!accessToken) return false;

  try {
    const response = await fetch(`${api.proxy.supabaseUrl}/functions/v1/api-keys`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: api.proxy.supabaseAnonKey,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return false;

    const { keys } = await response.json();
    // Check if zotero key exists
    return Array.isArray(keys) && keys.some((k: { service: string }) => k.service === 'zotero');
  } catch {
    return false;
  }
}

/**
 * Get Portal URL for redirects (e.g., to settings page)
 */
export function getPortalUrl(): string {
  return api.proxy.portalUrl;
}

/**
 * Generate return URL for Portal redirects
 */
export function getReturnUrl(): string {
  return encodeURIComponent(window.location.origin + window.location.pathname);
}

// =============================================================================
// EXPORTS
// =============================================================================

export const supabase = {
  getClient: getSupabaseClient,
  getSession,
  getAccessToken,
  isAuthenticated,
  signInWithEmail,
  signOut,
  proxyPandocRequest,
  isPandocConfigured,
  proxyZoteroRequest,
  isZoteroConfigured,
  getPortalUrl,
  getReturnUrl,
};

export default supabase;
